import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Globe } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

export const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <SEO 
        title="Privacy Policy - JustOffs" 
        description="Learn how JustOffs collects, uses, and protects your information. Your privacy is important to us." 
      />
      <Header />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-orange-500 mb-6 transition">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 mb-8 text-center">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-slate-400">Your privacy is important to us</p>
          <p className="text-slate-500 text-sm mt-2">Effective Date: 01/04/2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-8">
          {/* Intro */}
          <p className="text-slate-600 leading-relaxed">
            Welcome to JustOffs. Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your information when you use our platform.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Information We Collect
            </h2>
            <p className="text-slate-600 mb-3">We may collect the following information:</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Name, phone number, and email address
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Business details (shop name, location, offers)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Location data (if you use "near me" features)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Usage data (pages visited, interactions)
              </li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              How We Use Your Information
            </h2>
            <p className="text-slate-600 mb-3">We use your information to:</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Display your listings on the platform
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Help users discover nearby deals
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Improve our services and user experience
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Communicate updates or support responses
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
              Sharing of Information
            </h2>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
              <p className="text-green-700 font-medium">We do not sell your personal data.</p>
            </div>
            <p className="text-slate-600 mb-3">We may share limited information:</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                With users (public listings like business name, offers, location)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                With service providers (hosting, analytics, payments)
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">4</span>
              Payments
            </h2>
            <p className="text-slate-600">
              Payments (if any) are processed securely via third-party payment providers like Razorpay. We do not store your card or payment details.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">5</span>
              Cookies & Tracking
            </h2>
            <p className="text-slate-600 mb-3">We may use cookies to:</p>
            <ul className="space-y-2 text-slate-600 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Improve user experience
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Analyze traffic and usage patterns
              </li>
            </ul>
            <p className="text-slate-500 text-sm bg-slate-50 rounded-lg p-3">
              You can disable cookies in your browser settings.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">6</span>
              Data Security
            </h2>
            <p className="text-slate-600">
              We take reasonable steps to protect your data. However, no system is 100% secure.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">7</span>
              Your Rights
            </h2>
            <p className="text-slate-600 mb-3">You may:</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Request to update or delete your data
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Contact us for any privacy concerns
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">8</span>
              Third-Party Links
            </h2>
            <p className="text-slate-600">
              Our platform may contain links to external websites. We are not responsible for their content or privacy practices.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">9</span>
              Updates to Policy
            </h2>
            <p className="text-slate-600">
              We may update this policy from time to time. Changes will be posted on this page.
            </p>
          </section>

          {/* Section 10 - Contact */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">10</span>
              Contact Us
            </h2>
            <p className="text-slate-600 mb-4">For any questions:</p>
            <div className="bg-slate-50 rounded-xl p-5 space-y-3">
              <a href="mailto:admin@justoffs.com" className="flex items-center gap-3 text-slate-700 hover:text-orange-500 transition">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-orange-500" />
                </div>
                <span>admin@justoffs.com</span>
              </a>
              <a href="https://justoffs.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-orange-500 transition">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-orange-500" />
                </div>
                <span>https://justoffs.com/</span>
              </a>
            </div>
          </section>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};
